/**
 * Purchase-order line-items editor — section 3 of the composer.
 *
 * Faithful to the prototype's `.section` (step 3 · "Line items" · "Items
 * you're ordering. Qty and rate agreed with the vendor.") followed by the
 * `.items-table` and the in-card totals strip the prototype renders below
 * the table (Subtotal · GST · Total in a flex-end stack).
 *
 * Cells render bare `<input>` with focus-reveal; Unit + GST use `Select`
 * from `@flint/ui`; the Amount column is computed read-only mono text;
 * "Add line item" lives in the `<tfoot>`. Form state stays string-typed;
 * the composer maps it to the wire contract at submit time.
 *
 * @see prototype/purchases/purchase-order-new.html §"3 · Line items"
 */
import type { ChangeEvent } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import { paiseToInrExact } from "../../../lib/format.js";
import { computeLineTotalPaise, type PoTotals } from "../lib/po-math.js";
import { emptyPoLine, type PoLineItem } from "../lib/form-schema.js";
import { PoSectionCard } from "./po-section-card.js";
import { PoItemPicker } from "./po-item-picker.js";

const GST_OPTIONS = ["0", "0.25", "3", "5", "12", "18", "28", "40"] as const;
const UNIT_OPTIONS = ["pcs", "Nos", "kg", "m", "box", "set", "hr", "day", "lot"] as const;

const cellInput =
  "w-full rounded bg-transparent px-1 py-1 text-sm text-ink-2 " +
  "focus:bg-raised focus:outline-none focus:ring-1 focus:ring-line";
const cellInputMono = "font-mono";
const cellInputRight = "text-right";

interface PoLineItemsProps {
  rows: PoLineItem[];
  totals: PoTotals;
  error?: string | undefined;
  disabled?: boolean | undefined;
  onRowsChange: (rows: PoLineItem[]) => void;
}

export function PoLineItems({ rows, totals, error, disabled, onRowsChange }: PoLineItemsProps) {
  const updateRow = (id: string, patch: Partial<PoLineItem>) => {
    onRowsChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };
  const addRow = () => onRowsChange([...rows, emptyPoLine(rows.length + 1)]);
  const removeRow = (id: string) => onRowsChange(rows.filter((row) => row.id !== id));

  return (
    <PoSectionCard
      step="3"
      title="Line items"
      description="Items you're ordering. Qty and rate agreed with the vendor."
    >
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-raised text-sm uppercase tracking-wide text-muted">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                Item
              </th>
              <th scope="col" className="w-[88px] px-2 py-2 text-left font-medium">
                HSN
              </th>
              <th scope="col" className="w-[80px] px-2 py-2 text-right font-medium">
                Qty
              </th>
              <th scope="col" className="w-[88px] px-2 py-2 text-left font-medium">
                Unit
              </th>
              <th scope="col" className="w-[110px] px-2 py-2 text-right font-medium">
                Rate (₹)
              </th>
              <th scope="col" className="w-[80px] px-2 py-2 text-right font-medium">
                Disc%
              </th>
              <th scope="col" className="w-[100px] px-2 py-2 text-left font-medium">
                Tax %
              </th>
              <th scope="col" className="w-[120px] px-2 py-2 text-right font-medium">
                Amount
              </th>
              <th scope="col" className="w-[40px] px-2 py-2">
                <span className="sr-only">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-line">
                <td className="px-2 py-2">
                  <div className="flex items-center gap-1">
                    <PoItemPicker
                      disabled={disabled}
                      onPick={(values) => updateRow(row.id, values)}
                    />
                    <input
                      type="text"
                      aria-label={`Description ${row.id}`}
                      value={row.description}
                      onChange={onText(row.id, "description", updateRow)}
                      placeholder="Pick item or type description…"
                      className={`min-w-0 flex-1 ${cellInput}`}
                    />
                  </div>
                </td>
                <td className="px-1 py-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    aria-label={`HSN ${row.id}`}
                    value={row.hsn}
                    onChange={onText(row.id, "hsn", updateRow, {
                      normalize: (v) => v.replace(/\D/g, "").slice(0, 8),
                    })}
                    placeholder="0000"
                    className={`${cellInput} ${cellInputMono}`}
                  />
                </td>
                <td className="px-1 py-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    aria-label={`Quantity ${row.id}`}
                    value={row.qty}
                    onChange={onText(row.id, "qty", updateRow)}
                    className={`${cellInput} ${cellInputMono} ${cellInputRight}`}
                  />
                </td>
                <td className="px-1 py-2">
                  <Select value={row.unit} onValueChange={(v) => updateRow(row.id, { unit: v })}>
                    <SelectTrigger className="h-8 text-sm" aria-label={`Unit ${row.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map((u) => (
                        <SelectItem key={u} value={u}>
                          {u}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-1 py-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    aria-label={`Rate ${row.id}`}
                    value={row.rate}
                    onChange={onText(row.id, "rate", updateRow)}
                    placeholder="0.00"
                    className={`${cellInput} ${cellInputMono} ${cellInputRight}`}
                  />
                </td>
                <td className="px-1 py-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    aria-label={`Discount ${row.id}`}
                    value={row.discountRate}
                    onChange={onText(row.id, "discountRate", updateRow)}
                    className={`${cellInput} ${cellInputMono} ${cellInputRight}`}
                  />
                </td>
                <td className="px-1 py-2">
                  <Select value={row.taxRate} onValueChange={(v) => updateRow(row.id, { taxRate: v })}>
                    <SelectTrigger className="h-8 text-sm" aria-label={`GST slab ${row.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GST_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}%
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2 text-right font-mono text-sm text-ink">
                  {paiseToInrExact(computeLineTotalPaise(row)).replace("₹", "")}
                </td>
                <td className="px-1 py-2 text-center">
                  <button
                    type="button"
                    aria-label={`Remove ${row.id}`}
                    onClick={() => removeRow(row.id)}
                    disabled={rows.length === 1}
                    className="p-1 text-muted hover:text-bad disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-line bg-raised">
              <td colSpan={9} className="px-2 py-2">
                <Button type="button" intent="ghost" size="sm" onClick={addRow}>
                  <Plus size={14} />
                  <span>Add line item</span>
                </Button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {error ? <p className="mt-3 text-sm font-semibold text-bad">{error}</p> : null}

      {/* In-card totals strip — prototype's flex-end Subtotal / GST / Total stack */}
      <div className="mt-4 flex justify-end border-t border-line pt-4">
        <div className="min-w-[240px] text-sm">
          <div className="flex items-center justify-between py-1 text-muted">
            <span>Subtotal</span>
            <span className="font-mono font-medium text-ink">
              {paiseToInrExact(totals.subtotalPaise)}
            </span>
          </div>
          <div className="flex items-center justify-between py-1 text-muted">
            <span>GST</span>
            <span className="font-mono font-medium text-ink">
              {paiseToInrExact(totals.taxPaise)}
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between border-t border-line pb-1 pt-2 text-sm font-bold text-ink">
            <span>Total</span>
            <span className="font-mono">{paiseToInrExact(totals.totalPaise)}</span>
          </div>
        </div>
      </div>
    </PoSectionCard>
  );
}

function onText(
  id: string,
  field: keyof PoLineItem,
  updateRow: (id: string, patch: Partial<PoLineItem>) => void,
  opts?: { normalize?: (v: string) => string },
) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const value = opts?.normalize ? opts.normalize(raw) : raw;
    updateRow(id, { [field]: value } as Partial<PoLineItem>);
  };
}
